import { readPresenceActors } from '@blocksuite/store';

import type { DataViewSelection } from './types.js';
import type { SingleView } from './view-manager/index.js';

export interface DataViewRemoteRowFocus {
  clientId: number;
  color: string;
  rowId: string;
  updatedAt: number;
  userName?: string;
}

export interface DataViewRemoteCellPresence {
  caretLength?: number;
  caretOffset?: number;
  clientId: number;
  color: string;
  columnId: string;
  mode: 'selected' | 'editing';
  rowId: string;
  updatedAt: number;
  userAvatar?: string | null;
  userName?: string;
  /** Set when the cell is edited from outside a table view. */
  surface?: 'app-studio';
}

/**
 * A cell marker without a view id comes from a sender that has no table view
 * open; the cell is then marked in every view that shows the column.
 */
export function cellPresenceIsForView(
  presenceViewId: string | undefined,
  currentViewId: string | undefined
): boolean {
  return presenceViewId === undefined || presenceViewId === currentViewId;
}

/** Name shown next to a remote cell marker. */
export function remoteCellPresenceLabel(
  presence: Pick<DataViewRemoteCellPresence, 'userName' | 'surface'>
): string | undefined {
  if (!presence.userName) return undefined;
  return presence.surface === 'app-studio'
    ? `${presence.userName} (via App Studio)`
    : presence.userName;
}

type PresenceState =
  Parameters<typeof readPresenceActors>[0] extends ReadonlyMap<
    number,
    infer State
  >
    ? State
    : never;

const DEFAULT_PRESENCE_COLOR = 'var(--streamlain-v2-brand-primary)';

/**
 * Where assistants work in one database, as the markers a table shows for a
 * person: a cell marker when the assistant names a column, a row marker
 * otherwise. An assistant is shown to everyone, its owner included, in the
 * colour of its owner.
 */
export function readAssistantDatabasePresences(
  states: ReadonlyMap<number, PresenceState & { color?: string }>,
  databaseId: string
): {
  focuses: DataViewRemoteRowFocus[];
  cells: DataViewRemoteCellPresence[];
} {
  const focuses: DataViewRemoteRowFocus[] = [];
  const cells: DataViewRemoteCellPresence[] = [];
  for (const actor of readPresenceActors(states)) {
    const place = actor.databasePlace;
    if (actor.kind !== 'assistant' || place?.databaseId !== databaseId) {
      continue;
    }
    const marker = {
      clientId: actor.key,
      color: states.get(actor.ownerClientId)?.color ?? DEFAULT_PRESENCE_COLOR,
      rowId: place.rowId,
      // An assistant publishes no time; its markers sort before the people's.
      updatedAt: 0,
      userName: actor.name,
    };
    if (place.columnId) {
      cells.push({ ...marker, columnId: place.columnId, mode: 'editing' });
    } else {
      focuses.push(marker);
    }
  }
  return { focuses, cells };
}

export interface DataViewRemoteAreaSelectionPresence {
  clientId: number;
  color: string;
  columnIds: string[];
  rowIds: string[];
  updatedAt: number;
  userAvatar?: string | null;
  userName?: string;
}

export function areDataViewRemotePresencesEqual<T extends object>(
  previous: readonly T[],
  next: readonly T[]
): boolean {
  if (previous === next) return true;
  if (previous.length !== next.length) return false;

  return previous.every((previousPresence, index) => {
    const nextPresence = next[index];
    if (!nextPresence) return false;

    const previousRecord = previousPresence as Record<string, unknown>;
    const nextRecord = nextPresence as Record<string, unknown>;
    const previousKeys = Object.keys(previousRecord);
    const nextKeys = Object.keys(nextRecord);
    if (previousKeys.length !== nextKeys.length) return false;

    return previousKeys.every(key => {
      const previousValue = previousRecord[key];
      const nextValue = nextRecord[key];
      if (Array.isArray(previousValue) || Array.isArray(nextValue)) {
        return (
          Array.isArray(previousValue) &&
          Array.isArray(nextValue) &&
          previousValue.length === nextValue.length &&
          previousValue.every((value, valueIndex) =>
            Object.is(value, nextValue[valueIndex])
          )
        );
      }
      return Object.is(previousValue, nextValue);
    });
  });
}

export const DATA_VIEW_CELL_PRESENCE_EVENT = 'data-view-cell-presence';
export const DATA_VIEW_CELL_CARET_PRESENCE_EVENT =
  DATA_VIEW_CELL_PRESENCE_EVENT;

export interface DataViewCellPresenceDetail {
  caretLength?: number;
  caretOffset?: number;
  columnId: string;
  mode: 'selected' | 'editing';
  rowId: string;
  viewId: string;
}

export type DataViewCellCaretPresenceDetail = DataViewCellPresenceDetail;

export type DataViewCellPresence = {
  columnId: string;
  mode: 'selected' | 'editing';
  rowId: string;
  viewId: string;
};

export type DataViewAreaSelectionPresence = {
  columnIds: string[];
  rowIds: string[];
  viewId: string;
};

function isTableAreaFocus(selection: {
  columnsSelection: { end: number; start: number };
  focus: { columnIndex: number; rowIndex: number };
  rowsSelection: { end: number; start: number };
}) {
  return (
    selection.focus.rowIndex === selection.rowsSelection.start &&
    selection.focus.rowIndex === selection.rowsSelection.end &&
    selection.focus.columnIndex === selection.columnsSelection.start &&
    selection.focus.columnIndex === selection.columnsSelection.end
  );
}

export function getRowFocusIdFromSelection(
  selection: DataViewSelection | undefined,
  resolveRowByIndex: (viewId: string, rowIndex: number) => string | undefined
): string | undefined {
  if (!selection) {
    return;
  }

  if (selection.type === 'table') {
    if (selection.selectionType === 'row') {
      return selection.rows.at(-1)?.id;
    }
    if (!isTableAreaFocus(selection)) {
      return;
    }
    return resolveRowByIndex(selection.viewId, selection.focus.rowIndex);
  }

  if (selection.type === 'kanban') {
    if (selection.selectionType === 'card') {
      return selection.cards.at(-1)?.cardId;
    }
    if (selection.selectionType === 'cell') {
      return selection.cardId;
    }
    return;
  }

  if (
    selection.type === 'gantt' &&
    (selection.selectionType === 'row' ||
      selection.selectionType === 'bar' ||
      selection.selectionType === 'cell')
  ) {
    return selection.rowId;
  }

  if (
    selection.type === 'calendar' &&
    (selection.selectionType === 'event' || selection.selectionType === 'cell')
  ) {
    return selection.rowId;
  }

  return;
}

export function getCellPresenceFromSelection(
  selection: DataViewSelection | undefined,
  resolveRowByIndex: (viewId: string, rowIndex: number) => string | undefined,
  resolveColumnByIndex: (
    viewId: string,
    columnIndex: number
  ) => string | undefined
): DataViewCellPresence | undefined {
  if (!selection) {
    return;
  }

  if (selection.type === 'calendar') {
    if (selection.selectionType !== 'cell') {
      return;
    }
    return {
      columnId: selection.columnId,
      mode: selection.isEditing ? 'editing' : 'selected',
      rowId: selection.rowId,
      viewId: selection.viewId,
    };
  }

  if (selection.type !== 'table' || selection.selectionType !== 'area') {
    return;
  }
  if (!isTableAreaFocus(selection)) {
    return;
  }

  const rowId = resolveRowByIndex(selection.viewId, selection.focus.rowIndex);
  const columnId = resolveColumnByIndex(
    selection.viewId,
    selection.focus.columnIndex
  );
  if (!rowId || !columnId) {
    return;
  }

  return {
    columnId,
    mode: selection.isEditing ? 'editing' : 'selected',
    rowId,
    viewId: selection.viewId,
  };
}

export function getAreaSelectionPresenceFromSelection(
  selection: DataViewSelection | undefined,
  resolveRowByIndex: (viewId: string, rowIndex: number) => string | undefined,
  resolveColumnByIndex: (
    viewId: string,
    columnIndex: number
  ) => string | undefined
): DataViewAreaSelectionPresence | undefined {
  if (
    !selection ||
    selection.type !== 'table' ||
    selection.selectionType !== 'area' ||
    selection.isEditing ||
    isTableAreaFocus(selection)
  ) {
    return;
  }

  const rowStart = Math.min(
    selection.rowsSelection.start,
    selection.rowsSelection.end
  );
  const rowEnd = Math.max(
    selection.rowsSelection.start,
    selection.rowsSelection.end
  );
  const columnStart = Math.min(
    selection.columnsSelection.start,
    selection.columnsSelection.end
  );
  const columnEnd = Math.max(
    selection.columnsSelection.start,
    selection.columnsSelection.end
  );
  const rowIds: string[] = [];
  const columnIds: string[] = [];
  for (let rowIndex = rowStart; rowIndex <= rowEnd; rowIndex += 1) {
    const rowId = resolveRowByIndex(selection.viewId, rowIndex);
    if (rowId) rowIds.push(rowId);
  }
  for (
    let columnIndex = columnStart;
    columnIndex <= columnEnd;
    columnIndex += 1
  ) {
    const columnId = resolveColumnByIndex(selection.viewId, columnIndex);
    if (columnId) columnIds.push(columnId);
  }
  if (rowIds.length === 0 || columnIds.length === 0) {
    return;
  }
  return {
    columnIds,
    rowIds,
    viewId: selection.viewId,
  };
}

export function getColumnIdByViewIndex(
  view: SingleView | undefined,
  columnIndex: number
): string | undefined {
  if (!view || columnIndex < 0) {
    return;
  }
  return view.getBoundedPropertiesWindow().properties[columnIndex]?.id;
}

export function getRowIdByViewIndex(
  view: SingleView | undefined,
  rowIndex: number
): string | undefined {
  if (!view || rowIndex < 0) {
    return;
  }
  return view.getBoundedRowsWindow().rows[rowIndex]?.rowId;
}
